/**
 * Fixed Route Configuration Step Component
 * 
 * Configuration step for Fixed Routes mode - allows user to configure
 * vehicle sharing mode and vehicle specifications.
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Stack,
  InputAdornment,
  Paper,
  Divider,
  Select,
  MenuItem,
  InputLabel,
} from '@mui/material';

import Button from '@mui/material/Button';
import Switch from '@mui/material/Switch';
import Chip from '@mui/material/Chip';

import { ConfigurationStepProps } from './types';
import { useFixedRoutes } from '../../../../context/FixedRoutesContext';
import { logEvent } from '../../../../services/analytics';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../../../utils/iconFallbacks';
/**
 * Fixed Route Configuration Step Props
 */
interface FixedRouteConfigurationStepProps {
  config: ConfigurationStepProps['config'];
  onConfigChange: ConfigurationStepProps['onConfigChange'];
  availablePoints: ConfigurationStepProps['availablePoints'];
}

const FixedRouteConfigurationStep: React.FC<FixedRouteConfigurationStepProps> = ({
  config,
  onConfigChange,
  availablePoints,
}) => {
  const { t } = useTranslation('mapWizards');
  const { routes, trafficMultiplier } = useFixedRoutes();
  const [expandedSections, setExpandedSections] = useState({
    sharing: true,
    vehicle: true,
    shift: false,
  });
  
  const [shiftConfigErrors, setShiftConfigErrors] = useState<Record<string, string>>({});

  // Get selected routes
  const selectedRoutes = routes.filter((route) =>
    config.selectedRouteIds?.includes(route.id)
  );

  // Auto-default to shared mode when multiple routes selected
  // Only enforce this when the number of selected routes changes (route selection/de-selection)
  // NOT when the user manually changes the vehicle sharing mode
  useEffect(() => {
    // Only force shared mode if multiple routes are selected AND current mode is not already shared
    // This prevents overriding user's manual selection of "dedicated" mode
    if (selectedRoutes.length > 1) {
      // With multiple routes, dedicated mode doesn't make sense, so force shared
      if (config.vehicleSharingMode !== 'shared') {
        onConfigChange({ vehicleSharingMode: 'shared' });
      }
    }
    // If only one route is selected, allow either mode - don't force anything
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRoutes.length]); // Only depend on route count changes, ignore mode/config changes

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Validation function for shift configuration
  const handleValidateShiftConfig = (cfg: typeof config) => {
    const errors = validateShiftConfigInternal(cfg, t);
    setShiftConfigErrors(errors);
  };

  // Initialize validation on mount and when config changes
  useEffect(() => {
    handleValidateShiftConfig(config);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.maxShiftTimeMinutes, config.breakLengthMinutes, config.minTimeBeforeBreakMinutes, config.minTimeAfterBreakMinutes]);

  const handleSharingModeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newMode = event.target.value as 'dedicated' | 'shared';
    onConfigChange({ vehicleSharingMode: newMode });
    
    logEvent({
      category: 'AutoFleetGenerator',
      action: 'VehicleSharingModeChanged',
      label: newMode,
      routes_count: selectedRoutes.length,
    });
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('autoFleetGen.fixedRoute.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {t('autoFleetGen.fixedRoute.descriptionBase')}
        {selectedRoutes.length > 0 && (
          <span>{t(
            selectedRoutes.length === 1
              ? 'autoFleetGen.fixedRoute.descriptionRoutesOne'
              : 'autoFleetGen.fixedRoute.descriptionRoutesOther',
            { total: selectedRoutes.length }
          )}</span>
        )}
      </Typography>

      {/* Vehicle Sharing Mode Configuration */}
      <Accordion
        expanded={expandedSections.sharing}
        onChange={() => toggleSection('sharing')}
        sx={{ mb: 2 }}
      >
        <AccordionSummary expandIcon={<Icon size={20} name="chevron-down" />}>
          <Box display="flex" alignItems="center" gap={1}>
            <Icon size={20} d={ICON_FALLBACKS.Share} />
            <Typography variant="subtitle1">{t('autoFleetGen.fixedRoute.sharing.title')}</Typography>
            <Paper
              sx={{
                px: 1,
                py: 0.25,
                bgcolor: config.vehicleSharingMode === 'shared' ? '#e3f2fd' : '#fff3e0',
              }}
            >
              <Typography variant="caption" fontWeight={600}>
                {config.vehicleSharingMode === 'shared'
                  ? t('autoFleetGen.fixedRoute.sharing.shared')
                  : t('autoFleetGen.fixedRoute.sharing.dedicated')}
              </Typography>
            </Paper>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <FormControl component="fieldset">
              <FormLabel component="legend">{t('autoFleetGen.fixedRoute.sharing.question')}</FormLabel>
              <RadioGroup
                value={config.vehicleSharingMode || 'shared'}
                onChange={handleSharingModeChange}
                sx={{ mt: 1 }}
              >
                <Paper
                  sx={{
                    p: 2,
                    mb: 2,
                    border: config.vehicleSharingMode === 'shared' ? '2px solid #1976d2' : '1px solid #e0e0e0',
                    bgcolor: config.vehicleSharingMode === 'shared' ? '#e3f2fd' : 'white',
                    cursor: 'pointer',
                  }}
                  onClick={() => handleSharingModeChange({ target: { value: 'shared' } } as any)}
                >
                  <FormControlLabel
                    value="shared"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {t('autoFleetGen.fixedRoute.sharing.sharedTitle')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t('autoFleetGen.fixedRoute.sharing.sharedDescription')}
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0 }}
                  />
                </Paper>

                <Paper
                  sx={{
                    p: 2,
                    border: config.vehicleSharingMode === 'dedicated' ? '2px solid #1976d2' : '1px solid #e0e0e0',
                    bgcolor: config.vehicleSharingMode === 'dedicated' ? '#e3f2fd' : 'white',
                    cursor: 'pointer',
                  }}
                  onClick={() => handleSharingModeChange({ target: { value: 'dedicated' } } as any)}
                >
                  <FormControlLabel
                    value="dedicated"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {t('autoFleetGen.fixedRoute.sharing.dedicatedTitle')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t('autoFleetGen.fixedRoute.sharing.dedicatedDescription')}
                        </Typography>
                      </Box>
                    }
                    sx={{ m: 0 }}
                  />
                </Paper>
              </RadioGroup>
            </FormControl>

            {selectedRoutes.length === 1 && (
              <Alert severity="info" icon={<Icon size={20} name="circle-info" />}>
                <Typography variant="body2">
                  {t('autoFleetGen.fixedRoute.sharing.singleRouteInfo')}
                </Typography>
              </Alert>
            )}
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Vehicle Configuration */}
      <Accordion
        expanded={expandedSections.vehicle}
        onChange={() => toggleSection('vehicle')}
        sx={{ mb: 2 }}
      >
        <AccordionSummary expandIcon={<Icon size={20} name="chevron-down" />}>
          <Box display="flex" alignItems="center" gap={1}>
            <Icon size={20} name="gear" />
            <Typography variant="subtitle1">{t('autoFleetGen.fixedRoute.vehicle.title')}</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            {/* Seat Configuration */}
            <Typography variant="subtitle2" color="text.secondary">
              {t('autoFleetGen.fixedRoute.vehicle.capacity')}
            </Typography>
            <Box display="flex" gap={2}>
              <TextField
                label={t('autoFleetGen.fixedRoute.vehicle.standardSeats')}
                type="number"
                fullWidth
                value={config.standardSeats}
                onChange={(e) => onConfigChange({ standardSeats: parseInt(e.target.value, 10) || 0 })}
                inputProps={{ min: 0 }}
                helperText={t('autoFleetGen.fixedRoute.vehicle.standardSeatsHelper')}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon name="user" size={20} />
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                label={t('autoFleetGen.fixedRoute.vehicle.wheelchairSeats')}
                type="number"
                fullWidth
                value={config.wheelchairSeats}
                onChange={(e) => onConfigChange({ wheelchairSeats: parseInt(e.target.value, 10) || 0 })}
                inputProps={{ min: 0 }}
                helperText={t('autoFleetGen.fixedRoute.vehicle.wheelchairSeatsHelper')}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Icon name="wheelchair-move" size={20} />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Divider />

            {/* Location Configuration */}
            <Typography variant="subtitle2" color="text.secondary">
              {t('autoFleetGen.fixedRoute.vehicle.depotLocations')}
            </Typography>

            {availablePoints.length === 0 ? (
              <Alert severity="warning">
                <Typography variant="body2">
                  {t('autoFleetGen.fixedRoute.vehicle.noPoints')}
                </Typography>
              </Alert>
            ) : (
              <Box display="flex" gap={2}>
                <FormControl fullWidth>
                  <InputLabel>{t('autoFleetGen.fixedRoute.vehicle.startDepot')}</InputLabel>
                  <Select
                    value={config.startLocationId || ''}
                    onChange={(e) => onConfigChange({ startLocationId: e.target.value })}
                    label={t('autoFleetGen.fixedRoute.vehicle.startDepot')}
                    MenuProps={{ sx: { zIndex: 2600 } }}
                    startAdornment={
                      <InputAdornment position="start">
                        <Icon name="location-dot" size={20} />
                      </InputAdornment>
                    }
                  >
                    {availablePoints.map((point) => (
                      <MenuItem key={point.id} value={point.id}>
                        {point.name || point.id}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>{t('autoFleetGen.fixedRoute.vehicle.endDepot')}</InputLabel>
                  <Select
                    value={config.endLocationId || ''}
                    onChange={(e) => onConfigChange({ endLocationId: e.target.value })}
                    label={t('autoFleetGen.fixedRoute.vehicle.endDepot')}
                    MenuProps={{ sx: { zIndex: 2600 } }}
                    startAdornment={
                      <InputAdornment position="start">
                        <Icon name="location-dot" size={20} />
                      </InputAdornment>
                    }
                  >
                    {availablePoints.map((point) => (
                      <MenuItem key={point.id} value={point.id}>
                        {point.name || point.id}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}
          </Stack>
        </AccordionDetails>
      </Accordion>

      {/* Shift Configuration */}
      <Accordion
        expanded={expandedSections.shift}
        onChange={() => toggleSection('shift')}
        sx={{ mb: 2 }}
      >
        <AccordionSummary expandIcon={<Icon size={20} name="chevron-down" />}>
          <Box display="flex" alignItems="center" gap={1}>
            <Icon size={20} name="clock" />
            <Typography variant="subtitle1">{t('autoFleetGen.fixedRoute.shift.title')}</Typography>
            <Paper
              sx={{
                px: 1,
                py: 0.25,
                bgcolor: config.enableShiftAllocation ? '#e3f2fd' : '#f5f5f5',
              }}
            >
              <Typography variant="caption" fontWeight={600}>
                {config.enableShiftAllocation
                  ? t('autoFleetGen.fixedRoute.shift.enabled')
                  : t('autoFleetGen.fixedRoute.shift.disabled')}
              </Typography>
            </Paper>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              {t('autoFleetGen.fixedRoute.shift.description')}
            </Typography>

            {/* Enable Shift Allocation Toggle */}
            <Paper sx={{ p: 2, bgcolor: '#f9f9f9' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={config.enableShiftAllocation ?? false}
                    onChange={(e) => {
                      const enabled = e.target.checked;
                      onConfigChange({ enableShiftAllocation: enabled });
                      logEvent({
                        category: 'AutoFleetGenerator',
                        action: 'ShiftAllocationToggled',
                        label: enabled ? 'Enabled' : 'Disabled',
                      });
                    }}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" fontWeight={600}>
                      {t('autoFleetGen.fixedRoute.shift.enableLabel')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {config.enableShiftAllocation
                        ? t('autoFleetGen.fixedRoute.shift.enableHelperOn')
                        : t('autoFleetGen.fixedRoute.shift.enableHelperOff')}
                    </Typography>
                  </Box>
                }
              />
            </Paper>

            {config.enableShiftAllocation && (
              <>
                {/* Maximum Shift Time */}
            <Box>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                {t('autoFleetGen.fixedRoute.shift.maxShiftTime')}
              </Typography>
              <Box display="flex" gap={1} mb={1}>
                {[360, 480, 600, 720].map((minutes) => (
                  <Button
                    key={minutes}
                    variant={config.maxShiftTimeMinutes === minutes ? 'contained' : 'outlined'}
                    size="small"
                    onClick={() => {
                      const newConfig = { ...config, maxShiftTimeMinutes: minutes };
                      onConfigChange({ maxShiftTimeMinutes: minutes });
                      handleValidateShiftConfig(newConfig);
                    }}
                  >
                    {t('autoFleetGen.fixedRoute.shift.hours', { value: minutes / 60 })}
                  </Button>
                ))}
              </Box>
              <TextField
                label={t('autoFleetGen.fixedRoute.shift.maxShiftTime')}
                type="number"
                fullWidth
                value={config.maxShiftTimeMinutes || 480}
                onChange={(e) => {
                  const value = parseInt(e.target.value, 10) || 0;
                  const newConfig = { ...config, maxShiftTimeMinutes: value };
                  onConfigChange({ maxShiftTimeMinutes: value });
                  handleValidateShiftConfig(newConfig);
                }}
                error={!!shiftConfigErrors.maxShiftTime}
                helperText={shiftConfigErrors.maxShiftTime || t('autoFleetGen.fixedRoute.shift.maxShiftTimeHelper')}
                InputProps={{
                  endAdornment: <InputAdornment position="end">{t('autoFleetGen.fixedRoute.shift.minutes')}</InputAdornment>,
                }}
              />
            </Box>

            <Divider />

            {/* Break Configuration */}
            <Typography variant="subtitle2" color="text.secondary">
              {t('autoFleetGen.fixedRoute.shift.breakConfig')}
            </Typography>

            <TextField
              label={t('autoFleetGen.fixedRoute.shift.breakLength')}
              type="number"
              fullWidth
              value={config.breakLengthMinutes || 30}
                onChange={(e) => {
                const value = parseInt(e.target.value, 10) || 0;
                const newConfig = { ...config, breakLengthMinutes: value };
                onConfigChange({ breakLengthMinutes: value });
                handleValidateShiftConfig(newConfig);
              }}
              error={!!shiftConfigErrors.breakLength}
              helperText={shiftConfigErrors.breakLength || t('autoFleetGen.fixedRoute.shift.breakLengthHelper')}
              InputProps={{
                endAdornment: <InputAdornment position="end">{t('autoFleetGen.fixedRoute.shift.minutes')}</InputAdornment>,
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon name="coffee" size={20} />
                  </InputAdornment>
                ),
              }}
            />

            <Box display="flex" gap={2}>
              <TextField
                label={t('autoFleetGen.fixedRoute.shift.minBeforeBreak')}
                type="number"
                fullWidth
                value={config.minTimeBeforeBreakMinutes || 120}
                onChange={(e) => {
                  const value = parseInt(e.target.value, 10) || 0;
                  const newConfig = { ...config, minTimeBeforeBreakMinutes: value };
                  onConfigChange({ minTimeBeforeBreakMinutes: value });
                  handleValidateShiftConfig(newConfig);
                }}
                error={!!shiftConfigErrors.minTimeBeforeBreak}
                helperText={shiftConfigErrors.minTimeBeforeBreak || t('autoFleetGen.fixedRoute.shift.minBeforeBreakHelper')}
                InputProps={{
                  endAdornment: <InputAdornment position="end">{t('autoFleetGen.fixedRoute.shift.min')}</InputAdornment>,
                }}
              />
              <TextField
                label={t('autoFleetGen.fixedRoute.shift.minAfterBreak')}
                type="number"
                fullWidth
                value={config.minTimeAfterBreakMinutes || 120}
                onChange={(e) => {
                  const value = parseInt(e.target.value, 10) || 0;
                  const newConfig = { ...config, minTimeAfterBreakMinutes: value };
                  onConfigChange({ minTimeAfterBreakMinutes: value });
                  handleValidateShiftConfig(newConfig);
                }}
                error={!!shiftConfigErrors.minTimeAfterBreak}
                helperText={shiftConfigErrors.minTimeAfterBreak || t('autoFleetGen.fixedRoute.shift.minAfterBreakHelper')}
                InputProps={{
                  endAdornment: <InputAdornment position="end">{t('autoFleetGen.fixedRoute.shift.min')}</InputAdornment>,
                }}
              />
            </Box>

            <Divider />

            {/* Stagger Breaks */}
            <FormControlLabel
              control={
                <Switch
                  checked={config.staggerBreaks ?? true}
                  onChange={(e) => {
                    onConfigChange({ staggerBreaks: e.target.checked });
                    logEvent({
                      category: 'AutoFleetGenerator',
                      action: 'StaggerBreaksToggled',
                      label: e.target.checked ? 'Enabled' : 'Disabled',
                    });
                  }}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">{t('autoFleetGen.fixedRoute.shift.staggerBreaks')}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('autoFleetGen.fixedRoute.shift.staggerBreaksHelper')}
                  </Typography>
                </Box>
              }
            />

            <Divider />

            {/* Peak Periods Configuration */}
            <Typography variant="subtitle2" color="text.secondary">
              {t('autoFleetGen.fixedRoute.shift.peakPeriodsOptional')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t('autoFleetGen.fixedRoute.shift.peakPeriodsDescription')}
            </Typography>

            <Button
              variant="outlined"
              startIcon={<Icon size={20} name="plus" />}
              onClick={() => {
                const newPeak = { start: '07:00', end: '09:00' };
                onConfigChange({
                  peakPeriods: [...(config.peakPeriods || []), newPeak],
                });
                logEvent({
                  category: 'AutoFleetGenerator',
                  action: 'PeakPeriodAdded',
                  label: 'Peak Period Added',
                });
              }}
              fullWidth
            >
              {t('autoFleetGen.fixedRoute.shift.addPeak')}
            </Button>

            {config.peakPeriods && config.peakPeriods.length > 0 && (
              <>
                {config.peakPeriods.map((peak, index) => (
                  <Paper key={index} sx={{ p: 2, bgcolor: '#f9f9f9' }}>
                    <Box display="flex" alignItems="center" gap={2}>
                      <TextField
                        label={t('autoFleetGen.fixedRoute.shift.startTime')}
                        type="time"
                        value={peak.start || ''}
                        onChange={(e) => {
                          const newPeaks = config.peakPeriods?.map((p, i) =>
                            i === index ? { ...p, start: e.target.value } : p
                          ) || [];
                          onConfigChange({ peakPeriods: newPeaks });
                        }}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ lang: 'en-GB' }}
                        size="small"
                      />
                      <Typography variant="body2" color="text.secondary">
                        {t('autoFleetGen.fixedRoute.shift.to')}
                      </Typography>
                      <TextField
                        label={t('autoFleetGen.fixedRoute.shift.endTime')}
                        type="time"
                        value={peak.end || ''}
                        onChange={(e) => {
                          const newPeaks = config.peakPeriods?.map((p, i) =>
                            i === index ? { ...p, end: e.target.value } : p
                          ) || [];
                          onConfigChange({ peakPeriods: newPeaks });
                        }}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ lang: 'en-GB' }}
                        size="small"
                      />
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => {
                          const newPeaks = config.peakPeriods?.filter((_, i) => i !== index) || [];
                          onConfigChange({ peakPeriods: newPeaks });
                          logEvent({
                            category: 'AutoFleetGenerator',
                            action: 'PeakPeriodRemoved',
                            label: 'Peak Period Removed',
                          });
                        }}
                        startIcon={<Icon size={20} name="trash" />}
                      >
                        {t('autoFleetGen.fixedRoute.shift.remove')}
                      </Button>
                    </Box>
                  </Paper>
                ))}

                <Divider />

                <TextField
                  label={t('autoFleetGen.fixedRoute.shift.breakReduction')}
                  type="number"
                  fullWidth
                  value={config.breakReductionDuringPeakMinutes || 0}
                  onChange={(e) =>
                    onConfigChange({
                      breakReductionDuringPeakMinutes: parseInt(e.target.value, 10) || 0,
                    })
                  }
                  InputProps={{
                    endAdornment: <InputAdornment position="end">{t('autoFleetGen.fixedRoute.shift.minutes')}</InputAdornment>,
                  }}
                  helperText={t('autoFleetGen.fixedRoute.shift.breakReductionHelper')}
                />
              </>
            )}
              </>
            )}
            {!config.enableShiftAllocation && (
              <Alert severity="info" icon={<Icon size={20} name="circle-info" />}>
                <Typography variant="body2">
                  {t('autoFleetGen.fixedRoute.shift.disabledInfo')}
                </Typography>
              </Alert>
            )}
          </Stack>
        </AccordionDetails>
      </Accordion>

    </Box>
  );
};

// Validation function for shift configuration (internal helper)
function validateShiftConfigInternal(config: ConfigurationStepProps['config'], t: TFunction): Record<string, string> {
  const errors: Record<string, string> = {};

  // Validate max shift time
  const maxShiftTime = config.maxShiftTimeMinutes || 0;
  if (maxShiftTime <= 60) {
    errors.maxShiftTime = t('autoFleetGen.fixedRoute.shift.errors.maxShiftTimeMin');
  } else if (maxShiftTime >= 1440) {
    errors.maxShiftTime = t('autoFleetGen.fixedRoute.shift.errors.maxShiftTimeMax');
  }

  // Validate break length
  const breakLength = config.breakLengthMinutes || 0;
  if (breakLength < 0) {
    errors.breakLength = t('autoFleetGen.fixedRoute.shift.errors.breakLengthNegative');
  } else if (breakLength >= maxShiftTime && maxShiftTime > 0) {
    errors.breakLength = t('autoFleetGen.fixedRoute.shift.errors.breakLengthExceeds');
  }

  // Validate min time before/after break
  const minTimeBefore = config.minTimeBeforeBreakMinutes || 0;
  const minTimeAfter = config.minTimeAfterBreakMinutes || 0;

  if (minTimeBefore < 0) {
    errors.minTimeBeforeBreak = t('autoFleetGen.fixedRoute.shift.errors.minBeforeNegative');
  }
  if (minTimeAfter < 0) {
    errors.minTimeAfterBreak = t('autoFleetGen.fixedRoute.shift.errors.minAfterNegative');
  }

  // Validate combined constraint: break length + min time before + min time after <= max shift time
  if (maxShiftTime > 0 && breakLength + minTimeBefore + minTimeAfter > maxShiftTime) {
    errors.combined = t('autoFleetGen.fixedRoute.shift.errors.combined', {
      breakLength,
      before: minTimeBefore,
      after: minTimeAfter,
      sum: breakLength + minTimeBefore + minTimeAfter,
      max: maxShiftTime,
    });
  }

  return errors;
}

export default FixedRouteConfigurationStep;

